import { ComponentType, useCallback } from "react";

import { QuestionFallback } from "@/modules/session-survey/components/QuestionFallback";
import { RadioQuestion } from "@/modules/session-survey/components/questions/RadioQuestion";
import { TextQuestion } from "@/modules/session-survey/components/questions/TextQuestion";
import {
  isAutoAdvanceQuestion,
  isAutoAdvanceQuestionType,
} from "@/modules/session-survey/consts/isAutoAdvanceQuestion";
import { useSessionSurveyContext } from "@/modules/session-survey/context/useSessionSurveyContext";
import { SurveyAnswerValue } from "@/modules/session-survey/types/SurveyAnswerValue";
import { SurveyQuestion } from "@/modules/session-survey/types/SurveyQuestion";
import { SurveyQuestionComponentProps } from "@/modules/session-survey/types/SurveyQuestionComponentProps";

interface QuestionRendererProps {
  question: SurveyQuestion;
}

const questionRegistry: Record<
  string,
  ComponentType<SurveyQuestionComponentProps>
> = {
  radio: RadioQuestion,
  select: RadioQuestion,
  text: TextQuestion,
  text_field: TextQuestion,
  textarea: TextQuestion,
};

export const QuestionRenderer = ({ question }: QuestionRendererProps) => {
  const { answers, answerQuestion, setAnswer } = useSessionSurveyContext();
  const hasRegisteredComponent = Object.hasOwn(questionRegistry, question.type);
  const hasRenderableOptions =
    !isAutoAdvanceQuestionType(question.type) ||
    Boolean(question.options?.length);
  const QuestionComponent =
    hasRegisteredComponent && hasRenderableOptions
      ? questionRegistry[question.type]
      : QuestionFallback;
  const value = answers[question.id];

  const handleAnswer = useCallback(
    (answerValue: SurveyAnswerValue) => {
      if (isAutoAdvanceQuestion(question)) {
        answerQuestion(question.id, answerValue);
        return;
      }

      setAnswer(question.id, answerValue);
    },
    [answerQuestion, question, setAnswer],
  );

  return (
    <QuestionComponent
      key={question.id}
      question={question}
      value={value}
      onAnswer={handleAnswer}
    />
  );
};
